/*
  MacShack "Complete your setup" matching engine (used by assets/ms-product-addons.js).
  Pure functions: given the device on the page and the in-stock accessory pool, pick the
  accessories that fit it best, one card per need, each with a short reason.

  Scoring, best first:
    1  exact fit      the accessory names this model or size (iPhone 16 Pro case, 49mm protector)
    2  generation     chip, connector or charging match (Touch ID for Apple silicon, MagSafe 3, USB-C)
    3  fills a gap    something this device doesn't come with (screen for Mac mini, ports on a 2-port Mac)
    4  general        works with any Mac / iPhone / iPad
  Ties: colour that suits the device, then margin % (assets/ms-setup-margins.json), then best-selling.
*/
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MsSetup = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var LIGHT = ['white', 'silver', 'ivory', 'off-white', 'starlight', 'cloud', 'clear', 'transparent'];
  var DARK = ['black', 'graphite', 'space gray', 'space grey', 'midnight', 'grey', 'gray', 'charcoal', 'space black'];
  var LIGHT_DEVICES = ['silver', 'starlight', 'gold', 'light gold', 'green', 'blue', 'pink', 'yellow', 'orange', 'purple', 'sky blue', 'blush', 'citrus', 'white', 'cloud white'];
  var DARK_DEVICES = ['space black', 'space gray', 'space grey', 'midnight', 'graphite', 'black', 'jet black', 'indigo', 'deep blue'];

  function lc(s) { return (s || '').toString().toLowerCase(); }
  function has(s, re) { return re.test(s); }

  /* ---------------- Device ---------------- */
  function device(d) {
    var t = lc(d.title).replace(/″|”|“/g, '"').replace(/-inch/g, '"').replace(/ inch/g, '"');
    var proc = lc(d.processor);
    var dev = { title: t, fam: '', model: '', size: 0, chip: '', silicon: false, intel: false, year: parseInt(d.year, 10) || 0,
                colour: lc(d.colour), ports: 0, magsafe: false, usbc: false, line: '', gen: '', mm: 0, series: '' };
    var first = t.split(/\s+/)[0];
    var mChip = t.match(/\bm([1-9])(\s*(pro|max|ultra))?\b/);
    if (mChip) { dev.chip = 'm' + mChip[1] + (mChip[3] ? ' ' + mChip[3] : ''); dev.silicon = true; }
    if (/intel|\bi[3579]\b|core i/.test(t + ' ' + proc)) { dev.intel = true; dev.silicon = false; }
    var mSize = t.match(/(\d{2}(?:\.\d)?)\s*"/);
    if (mSize) dev.size = parseFloat(mSize[1]);

    if (/^iphone|apple iphone/.test(t)) {
      dev.fam = 'iphone';
      dev.model = iphoneModels(t)[0] || '';
      var n = parseInt(dev.model, 10);
      dev.usbc = n >= 15 || /^1[5-9]e?\b/.test(dev.model);
      dev.magsafe = n >= 12;
    } else if (/^ipad|apple ipad/.test(t)) {
      dev.fam = 'ipad';
      dev.line = /ipad pro/.test(t) ? 'pro' : /ipad air/.test(t) ? 'air' : /ipad mini/.test(t) ? 'mini' : 'ipad';
      var g = t.match(/(\d{1,2})(?:st|nd|rd|th)\s*gen/);
      dev.gen = g ? parseInt(g[1], 10) : 0;
      dev.model = dev.line + (dev.size ? ' ' + dev.size : '');
    } else if (/apple watch|^watch/.test(t)) {
      dev.fam = 'watch';
      var mm = t.match(/(\d{2})\s*mm/); dev.mm = mm ? parseInt(mm[1], 10) : 0;
      dev.series = /ultra/.test(t) ? 'ultra' : (t.match(/series\s*(\d{1,2})/) || [])[1] || (/\bse\b/.test(t) ? 'se' : '');
    } else if (first === 'macbook' || /^(apple )?macbook/.test(t)) {
      dev.fam = 'macbook';
      dev.line = /macbook air/.test(t) ? 'air' : /macbook pro/.test(t) ? 'pro' : /macbook neo/.test(t) ? 'neo' : '';
      dev.usbc = true;
      dev.ports = dev.line === 'air' || dev.line === 'neo' || (dev.line === 'pro' && dev.size === 13) ? 2 : 0;
      var cn = mChip ? parseInt(mChip[1], 10) : 0;
      dev.magsafe = (dev.line === 'air' && cn >= 2) || (dev.line === 'pro' && (dev.size === 14 || dev.size === 16) && dev.silicon);
    } else if (/^(apple )?imac/.test(t)) {
      dev.fam = 'imac';
      dev.usbc = dev.silicon;
      var gpu = (proc.match(/(\d+)\s*c(?:ore)?\s*gpu/) || [])[1];
      gpu = gpu ? parseInt(gpu, 10) : 0;
      if (dev.silicon && gpu) {
        dev.ports = (dev.chip === 'm1' && gpu <= 7) || (dev.chip !== 'm1' && gpu <= 8) ? 2 : 4;
      }
    } else if (/mac mini/.test(t)) {
      dev.fam = 'macmini';
    } else if (/mac studio/.test(t) && !/studio display/.test(t)) {
      dev.fam = 'macstudio';
    } else if (/studio display/.test(t)) {
      dev.fam = 'display';
    } else if (/airpods/.test(t)) {
      dev.fam = 'airpods';
    }
    dev.mac = dev.fam === 'macbook' || dev.fam === 'imac' || dev.fam === 'macmini' || dev.fam === 'macstudio';
    dev.label = d.short || '';
    return dev;
  }

  /* All iPhone models an accessory title names: "iPhone 16 Pro/17/17 Pro" -> ["16 pro","17","17 pro"]. */
  function iphoneModels(t) {
    var out = [];
    var re = /iphone\s*([0-9xse][^()|,–-]*)/g, m;
    while ((m = re.exec(t))) {
      var parts = m[1].split(/\s*[\/&]\s*/), base = '';
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].trim();
        var mm = p.match(/^(1[0-9]e?|[6-9]s?|xs|xr|x|se)?\s*(pro max|pro|plus|mini|max)?(?=\s|$|\()/);
        if (!mm || (!mm[1] && !mm[2])) break;
        var num = mm[1] || base;
        if (!num) break;
        base = num;
        out.push((num + (mm[2] ? ' ' + mm[2] : '')).trim());
      }
    }
    return out;
  }

  /* ---------------- Accessory ---------------- */
  function item(raw) {
    var t = lc(raw.t).replace(/″|”|“/g, '"').replace(/-inch/g, '"').replace(/ inch/g, '"').replace(/‑|–/g, '-');
    var ty = lc(raw.ty), vn = lc(raw.vn), tg = lc(raw.tg);
    var a = { raw: raw, t: t, cat: '', apple: /^apple\b/.test(t) || vn === 'apple', iphones: [], colour: '' };
    a.microsoft = /microsoft|^surface\b/.test(t) || vn === 'microsoft';
    a.forIpad = /\bipad\b|folio|tablet/.test(t);
    a.forIphone = /\biphone\b/.test(t);
    a.forWatch = /apple watch|\bwatch\b/.test(t);
    a.iphones = iphoneModels(t);
    var c;
    for (var i = 0; i < DARK.length; i++) if (t.indexOf(DARK[i]) !== -1) { a.colour = 'dark'; break; }
    for (i = 0; i < LIGHT.length; i++) if (t.indexOf(LIGHT[i]) !== -1) { a.colour = a.colour ? '' : 'light'; break; }

    if (/warranty|insurance|application fee|gift card|shipping protection/.test(t) || raw.p === 0) a.cat = 'skip';
    else if (/webcam/.test(t)) a.cat = 'webcam';
    else if (/combo/.test(t) && /keyboard|mouse/.test(t)) a.cat = 'combo';
    else if (/trackpad/.test(t)) a.cat = 'trackpad';
    else if (/\bmouse\b/.test(t) && !/mouse pad|mousepad/.test(t)) a.cat = 'mouse';
    else if (/keyboard|keypad|type cover/.test(t)) a.cat = a.forIpad ? 'ipad-keyboard' : 'keyboard';
    else if (/pencil|\bpen\b|stylus/.test(t)) a.cat = 'pencil';
    else if (ty === 'monitor' || /\bmonitor\b|zenscreen/.test(t)) a.cat = 'monitor';
    else if (/\bhub\b|\bdock\b|multiport|docking/.test(t)) a.cat = 'hub';
    else if (/hdmi|displayport/.test(t) && /adapter|cable/.test(t)) a.cat = 'display-adapter';
    else if (/ssd|hard drive|enclosure|\bnvme\b|flash drive/.test(t) || ty === 'storage') a.cat = 'storage';
    else if (ty === 'bags' || /backpack|sleeve|laptop case|notebook case|topload|messenger/.test(t)) a.cat = 'bag';
    else if (/lens protector|camera lens/.test(t)) a.cat = 'lens';
    else if (/\bcase\b|cover|folio|shell/.test(t) && (a.forIphone || a.forIpad || a.forWatch)) a.cat = 'case';
    else if (/screen protector|protection|protector|glass/.test(t)) a.cat = 'protector';
    else if (a.forWatch && /charg/.test(t)) a.cat = 'watch-charger';
    else if (/power adapter|wall charger|charger|charging station|power bank|powerbank|power supply/.test(t)) a.cat = 'charger';
    else if (/cable/.test(t)) a.cat = 'cable';
    else if (/airpods|earbuds|headphones|earphones|beats|speaker/.test(t)) a.cat = 'audio';
    else if (/airtag/.test(t)) a.cat = 'airtag';
    else if (/cleaner|cleaning|desk mat/.test(t)) a.cat = 'care';
    else if (/adapter|dongle/.test(t)) a.cat = 'adapter';
    else a.cat = 'other';
    var w = t.match(/(\d{2,3})\s*w\b/); a.watts = w ? parseInt(w[1], 10) : 0;
    a.magsafe3 = /magsafe 3/.test(t);
    a.magsafe = /magsafe|qi2/.test(t) && !a.magsafe3;
    a.lightning = /lightning/.test(t);
    a.usbc = /usb-c|usb c|type-c|type c/.test(t);
    a.gaming = /gaming|lightspeed|superstrike/.test(t);
    a.silicon = /apple silicon/.test(t);
    var sz = t.match(/(\d{2})(?:\s*-\s*(\d{2}))?\s*"/);
    a.sizeMin = sz ? parseInt(sz[1], 10) : 0; a.sizeMax = sz ? parseInt(sz[2] || sz[1], 10) : 0;
    var mm = t.match(/(\d{2})\s*mm/g); a.mm = mm ? mm.map(function (x) { return parseInt(x, 10); }) : [];
    return a;
  }

  /* ---------------- Fit rules per need ---------------- */
  // Each returns null (doesn't fit) or [tier, why].
  var FAMNAME = { imac: 'iMac', macmini: 'Mac mini', macstudio: 'Mac Studio', macbook: 'MacBook', iphone: 'iPhone', ipad: 'iPad', watch: 'Apple Watch' };
  var MACLINE = { imac: 'iMac', macmini: 'Mac mini', macstudio: 'Mac Studio', macbook: 'MacBook' };

  function chipLabel(dev) { return dev.chip ? dev.chip.toUpperCase().replace(' PRO', ' Pro').replace(' MAX', ' Max') + ' ' : ''; }
  function macName(dev) { return MACLINE[dev.fam] || 'Mac'; }

  var FIT = {
    keyboard: function (a, dev, ctx) {
      if (a.cat !== 'keyboard' && a.cat !== 'combo') return null;
      if (!dev.mac || a.microsoft || a.forIpad) return null;
      if (ctx.upgrade) {
        if (!a.apple || a.cat === 'combo') return null;
        if (a.silicon || /touch id/.test(a.t)) return dev.silicon ? [2, 'Upgrade: Touch ID works with this ' + chipLabel(dev) + macName(dev)] : null;
        return [3, 'Upgrade from the Logitech keyboard'];
      }
      if (/touch id/.test(a.t) || a.silicon) return dev.silicon ? [2, 'Touch ID works with this ' + chipLabel(dev) + macName(dev)] : null;
      if (/numeric keypad/.test(a.t)) return [4, 'Adds a number pad'];
      if (a.cat === 'combo') return [ctx.needsInput ? 3 : 4, 'Keyboard and mouse in one'];
      if (a.apple) return [3, 'Made for Mac'];
      if (/mac/.test(a.t) || /keychron/.test(a.t)) return [4, 'Mac layout, wireless'];
      return [4, 'Works with any Mac'];
    },
    mouse: function (a, dev, ctx) {
      if (a.cat !== 'mouse' && a.cat !== 'trackpad') return null;
      if (!dev.mac || a.microsoft) return null;
      if (ctx.upgrade) {
        if (!a.apple) return null;
        return [2, a.cat === 'trackpad' ? 'Upgrade: Multi-Touch gestures, made for Mac' : 'Upgrade: Multi-Touch, made for Mac'];
      }
      if (a.apple) return [3, a.cat === 'trackpad' ? 'Multi-Touch gestures, made for Mac' : 'Made for Mac'];
      if (a.gaming) return [5, 'Wireless gaming mouse'];
      return [4, 'Works with any Mac'];
    },
    hub: function (a, dev) {
      if (a.cat !== 'hub') return null;
      if (!dev.mac || a.microsoft) return null;
      if (dev.intel && dev.fam === 'imac') return null;
      if (dev.ports === 2) return [3, 'This ' + macName(dev) + ' has 2 ports. Adds more'];
      return [4, 'More ports for your desk'];
    },
    monitor: function (a, dev) {
      if (a.cat !== 'monitor' || a.microsoft) return null;
      if (dev.fam === 'macmini' || dev.fam === 'macstudio') return [3, macName(dev) + ' has no screen. Mac compatible'];
      if (dev.fam === 'macbook') return [4, 'A bigger screen for your desk'];
      return null;
    },
    webcam: function (a, dev) {
      if (a.cat !== 'webcam') return null;
      return dev.fam === 'macmini' || dev.fam === 'macstudio' ? [3, 'For video calls. ' + macName(dev) + ' has no camera'] : null;
    },
    storage: function (a, dev) {
      if (a.cat !== 'storage' || !dev.mac) return null;
      return [4, 'Extra storage over USB-C'];
    },
    care: function (a, dev) {
      if (a.cat !== 'care') return null;
      if (/desk mat/.test(a.t)) return dev.mac && dev.fam !== 'macbook' ? [5, 'Finishes the desk'] : null;
      return [4, 'Keeps the screen spotless'];
    },
    bag: function (a, dev) {
      if (a.cat !== 'bag' || dev.fam !== 'macbook') return null;
      if (!a.sizeMin || !dev.size) return [4, 'Carries a MacBook'];
      var s = Math.round(dev.size);
      return s >= a.sizeMin && s <= a.sizeMax ? [1, 'Fits a ' + s + '″ MacBook'] : null;
    },
    power: function (a, dev) {
      if (dev.fam !== 'macbook') return null;
      if (a.cat === 'cable' && a.magsafe3) return dev.magsafe ? [2, 'Charges this MacBook over MagSafe 3'] : null;
      if (a.cat === 'charger' && a.watts >= 30 && !a.microsoft && !/car charger|power bank|powerbank/.test(a.t)) {
        // Apple's own range per model: [smallest that charges well, largest that adds anything]
        var rng = dev.size >= 16 ? [140, 140] : (dev.line === 'pro' && dev.size >= 14 ? [70, 100] : (dev.size >= 15 ? [35, 70] : [30, 70]));
        if (a.watts < rng[0]) return null;
        if (a.watts <= rng[1]) return [a.apple ? 2 : 3, a.watts + 'W, the right power for this MacBook'];
        return [3, a.watts + 'W, more than this MacBook needs'];
      }
      if (a.cat === 'cable' && a.usbc && !a.lightning && a.watts >= 60) return [4, 'USB-C charge cable'];
      return null;
    },
    'iphone-protect': function (a, dev) {
      if (dev.fam !== 'iphone' || !dev.model) return null;
      if ((a.cat === 'protector' || a.cat === 'case') && a.iphones.indexOf(dev.model) !== -1) {
        return [1, (a.cat === 'case' ? 'Case' : 'Screen protection') + ' made for iPhone ' + title(dev.model)];
      }
      return null;
    },
    'iphone-lens': function (a, dev) {
      if (dev.fam !== 'iphone' || a.cat !== 'lens') return null;
      return a.iphones.indexOf(dev.model) !== -1 ? [1, 'Made for iPhone ' + title(dev.model) + ' cameras'] : null;
    },
    'iphone-power': function (a, dev) {
      if (dev.fam !== 'iphone') return null;
      if (a.cat === 'cable' || a.cat === 'charger') {
        if (a.microsoft || a.magsafe3) return null;
        if (a.forIphone && a.iphones.length && a.iphones.indexOf(dev.model) === -1) return null;
        if (a.lightning && !a.usbc) return dev.usbc ? null : [2, 'Lightning, like this iPhone'];
        if (a.lightning && a.usbc) return dev.usbc ? null : [2, 'USB-C to Lightning for this iPhone'];
        if (a.magsafe && dev.magsafe) return [3, 'MagSafe charging for this iPhone'];
        if (a.cat === 'cable' && a.usbc) return dev.usbc ? [2, 'USB-C, like this iPhone'] : null;
        if (a.cat === 'charger' && a.usbc && a.watts >= 20 && a.watts <= 70 && !/car/.test(a.t)) return [4, a.watts + 'W fast charging'];
      }
      return null;
    },
    audio: function (a, dev) {
      if (a.cat !== 'audio' || !(dev.fam === 'iphone' || dev.fam === 'ipad')) return null;
      if (/speaker/.test(a.t)) return null;
      if (/airpods/.test(a.t)) return [3, 'Pairs instantly with your ' + (dev.fam === 'iphone' ? 'iPhone' : 'iPad')];
      if (/beats/.test(a.t)) return [4, 'One-tap pairing with Apple devices'];
      return [5, 'Wireless earbuds'];
    },
    airtag: function (a, dev) {
      return a.cat === 'airtag' && dev.fam === 'iphone' ? [4, 'Find your things with iPhone'] : null;
    },
    'ipad-keyboard': function (a, dev) {
      if (dev.fam !== 'ipad' || a.cat !== 'ipad-keyboard') return null;
      return ipadFit(a, dev) ? [1, 'Made for iPad ' + ipadName(dev)] : null;
    },
    'ipad-protect': function (a, dev) {
      if (dev.fam !== 'ipad' || (a.cat !== 'protector' && a.cat !== 'case')) return null;
      return a.forIpad && ipadFit(a, dev) ? [1, (a.cat === 'case' ? 'Case' : 'Screen protection') + ' made for iPad ' + ipadName(dev)] : null;
    },
    pencil: function (a, dev) {
      if (dev.fam !== 'ipad' || a.cat !== 'pencil' || a.microsoft) return null;
      if (/tips/.test(a.t)) return null;
      var need = pencilFor(dev);
      if (!need) return null;
      if (need === 'pro' && /pencil pro/.test(a.t)) return [2, 'Apple Pencil Pro works with this iPad'];
      if (need === '2' && /2nd gen/.test(a.t)) return [2, 'Pairs and charges on this iPad'];
      if (need === 'usb-c' && /usb-c/.test(a.t)) return [2, 'Works with this iPad'];
      if (need === '1' && /1st gen/.test(a.t)) return [2, 'Works with this iPad'];
      return null;
    },
    'ipad-power': function (a, dev) {
      if (dev.fam !== 'ipad') return null;
      if (a.cat === 'cable' && a.usbc && !a.lightning && !a.magsafe3) return [4, 'USB-C charge cable'];
      if (a.cat === 'charger' && a.usbc && a.watts >= 20 && a.watts <= 70 && !/car/.test(a.t) && !a.microsoft) return [4, a.watts + 'W fast charging'];
      return null;
    },
    'watch-protect': function (a, dev) {
      if (dev.fam !== 'watch' || (a.cat !== 'protector' && a.cat !== 'case')) return null;
      if (!a.forWatch) return null;
      var ultra = /ultra/.test(a.t);
      if (dev.series === 'ultra') return ultra ? [1, 'Made for Apple Watch Ultra'] : null;
      if (ultra || !dev.mm || a.mm.indexOf(dev.mm) === -1) return null;
      var s = parseInt(dev.series, 10), as = parseInt((a.t.match(/series\s*(\d{1,2})/) || [])[1], 10);
      if (s && as) {
        var grp = function (n) { return n >= 10 ? 10 : n >= 7 ? 7 : n >= 4 ? 4 : 1; };
        if (grp(s) !== grp(as)) return null;
      }
      return [1, 'Made for ' + dev.mm + 'mm Apple Watch'];
    },
    'watch-power': function (a, dev) {
      return dev.fam === 'watch' && a.cat === 'watch-charger' ? [2, 'Fast-charges Apple Watch'] : null;
    }
  };

  function title(m) { return m.replace(/\b(pro max|pro|plus|mini|max|e)\b/g, function (w) { return w === 'e' ? 'e' : w.replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }); }
  function ipadName(dev) {
    var l = dev.line === 'ipad' ? '' : (dev.line === 'pro' ? 'Pro' : dev.line === 'air' ? 'Air' : 'mini');
    return (l + (dev.size ? ' ' + dev.size + '″' : '')).trim() || '';
  }
  function ipadFit(a, dev) {
    var t = a.t;
    if (dev.line === 'pro' && !/ipad pro/.test(t)) return false;
    if (dev.line === 'air' && !/ipad air/.test(t)) return false;
    if (dev.line === 'mini' && !/ipad mini/.test(t)) return false;
    if (dev.line === 'ipad') {
      if (/ipad (pro|air|mini)/.test(t) && !/ipad \d|ipad (7|8|9|10)th/.test(t)) return false;
      if (dev.gen) {
        var gens = (t.match(/(\d{1,2})(?:st|nd|rd|th)/g) || []).map(function (x) { return parseInt(x, 10); });
        if (gens.length && gens.indexOf(dev.gen) === -1) return false;
      }
    }
    if (dev.size) {
      var s = (t.match(/(\d{2}(?:\.\d)?)\s*"/) || [])[1];
      if (s && parseFloat(s) !== dev.size) return false;
    }
    var m45 = /\bm4\b|\bm5\b/.test(t), devM45 = /m4|m5/.test(dev.chip);
    if (dev.line === 'pro' && m45 !== devM45 && (m45 || dev.size === 13)) return false;
    return true;
  }
  function pencilFor(dev) {
    var n = dev.chip ? parseInt(dev.chip.slice(1), 10) : 0;
    if (dev.line === 'pro') return n >= 4 ? 'pro' : '2';
    if (dev.line === 'air') return n >= 2 ? 'pro' : (n === 1 || /4th|5th/.test(dev.title) ? '2' : '');
    if (dev.line === 'mini') return /a17/.test(dev.title) ? 'pro' : (/6th/.test(dev.title) ? '2' : '');
    if (dev.line === 'ipad') return dev.gen >= 10 ? 'usb-c' : (dev.gen >= 6 ? '1' : '');
    return '';
  }

  /* Needs per device, in order. Extra needs fill in when one has nothing in stock. */
  var NEEDS = {
    imac:      [['keyboard', 'Keyboard'], ['mouse', 'Mouse'], ['hub', 'Hub'], ['care', 'Screen care'], ['storage', 'Storage']],
    macmini:   [['monitor', 'Display'], ['keyboard', 'Keyboard'], ['mouse', 'Mouse'], ['hub', 'Hub'], ['webcam', 'Webcam'], ['storage', 'Storage']],
    macstudio: [['monitor', 'Display'], ['keyboard', 'Keyboard'], ['mouse', 'Mouse'], ['hub', 'Hub'], ['webcam', 'Webcam'], ['storage', 'Storage']],
    macbook:   [['hub', 'Hub'], ['power', 'Charging'], ['bag', 'Bag'], ['mouse', 'Mouse'], ['monitor', 'Display'], ['storage', 'Storage']],
    iphone:    [['iphone-protect', 'Protection'], ['iphone-lens', 'Camera protection'], ['iphone-power', 'Charging'], ['audio', 'Audio'], ['airtag', 'AirTag']],
    ipad:      [['ipad-keyboard', 'Keyboard'], ['pencil', 'Apple Pencil'], ['ipad-protect', 'Protection'], ['ipad-power', 'Charging'], ['audio', 'Audio']],
    watch:     [['watch-protect', 'Protection'], ['watch-power', 'Charging']]
  };

  /* Needs where the accessory's colour is part of the look. */
  var LOOKS = { keyboard: 1, mouse: 1, hub: 1, bag: 1, 'iphone-protect': 1, 'ipad-keyboard': 1, 'ipad-protect': 1, 'watch-protect': 1, monitor: 1 };

  function colourBonus(a, dev) {
    if (!a.colour || !dev.colour) return 0;
    var light = LIGHT_DEVICES.indexOf(dev.colour) !== -1, dark = DARK_DEVICES.indexOf(dev.colour) !== -1;
    if (light && a.colour === 'light') return 1;
    if (dark && a.colour === 'dark') return 1;
    return 0;
  }
  function colourName(dev) {
    return dev.colour ? dev.colour.replace(/\b\w/g, function (c) { return c.toUpperCase(); }) : '';
  }

  /*
    pick(deviceInfo, pool, opts) -> [{ key, label, items: [{ raw, why, tier }] }]
    deviceInfo: { title, processor, colour, year }
    pool: [{ vid, h, t, ty, vn, tg, p, c, img, u, one, rank }]
    opts: { max: 4, perNeed: 5, upgrade: bool, exclude: {vid:true}, margins: {handle: pct}, pins: [vid] }
  */
  function pick(info, pool, opts) {
    opts = opts || {};
    var dev = device(info);
    var needs = NEEDS[dev.fam];
    if (!needs) return { device: dev, needs: [] };
    var items = pool.map(item);
    var margins = opts.margins || {};
    var ctx = { upgrade: !!opts.upgrade && dev.fam === 'imac', needsInput: dev.fam === 'macmini' || dev.fam === 'macstudio' };
    var used = {}, out = [];
    var max = opts.max || 4, per = opts.perNeed || 5;

    for (var n = 0; n < needs.length && out.length < max; n++) {
      var key = needs[n][0], fit = FIT[key], list = [];
      for (var i = 0; i < items.length; i++) {
        var a = items[i], r = a.raw;
        if (a.cat === 'skip' || used[r.vid] || (opts.exclude && opts.exclude[r.vid])) continue;
        if (dev.mac || dev.fam === 'iphone' || dev.fam === 'ipad' || dev.fam === 'watch') { if (a.microsoft) continue; }
        var f = fit(a, dev, ctx);
        if (!f) continue;
        var cb = LOOKS[key] ? colourBonus(a, dev) : 0;
        var why = f[1];
        if (cb && f[0] >= 3 && dev.colour) why = why + ' · suits a ' + colourName(dev) + ' ' + FAMNAME[dev.fam];
        list.push({ raw: r, why: why, tier: f[0], cb: cb, margin: margins[r.h] == null ? -1 : margins[r.h], rank: r.rank || 0 });
      }
      if (!list.length) continue;
      list.sort(function (x, y) {
        return (x.tier - y.tier) || (y.cb - x.cb) || (y.margin - x.margin) || (x.rank - y.rank);
      });
      // one design per card: drop colour duplicates of the same product line
      var seenName = {}, picked = [];
      for (var k = 0; k < list.length && picked.length < per; k++) {
        var base = list[k].raw.t.replace(/\s*[-–(]\s*(black|white|silver|grey|gray|space gr[ae]y|graphite|pink|blue|green|yellow|red|rose|tonal \w+|ivory white|off-white|retro \w+|lunar eclipse|titanium)\)?\s*(-\s*(new|pre-owned|demo))?\s*$/i, '').toLowerCase();
        if (seenName[base]) continue;
        seenName[base] = true;
        picked.push(list[k]);
      }
      picked.forEach(function (p) { used[p.raw.vid] = true; });
      out.push({ key: key, label: needs[n][1], items: picked });
    }
    return { device: dev, needs: out };
  }

  return { pick: pick, device: device, item: item, iphoneModels: iphoneModels, NEEDS: NEEDS };
});
